import { useEffect, useRef } from 'react';

/**
 * Holds the Screen Wake Lock while `active` is true.
 *
 * Without it a tablet sleeps after ~1 minute of no touch, which suspends the
 * page and kills the in-flight generation — a paid Gemini call lost with the
 * customer standing there. Store devices are meant to be configured never to
 * sleep, but that is an onboarding step, not a guarantee, and this is the
 * in-app backstop.
 *
 * Things that are only true on a real device:
 *
 * - Requires a secure context (HTTPS) and iOS 16.4+. On an unsupported browser
 *   `navigator.wakeLock` is simply absent and this is a no-op.
 * - The lock is **released automatically whenever the page is hidden**, and is
 *   not restored on return — hence the `visibilitychange` re-acquire.
 * - `request()` rejects with NotAllowedError on low battery or if the document
 *   is not visible. That is normal, not an error worth surfacing.
 * - On a laptop this does nothing observable, so the whole file is untestable
 *   outside real hardware.
 */
export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active) return;
    if (!('wakeLock' in navigator)) return;

    let cancelled = false;

    const acquire = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      if (sentinelRef.current && !sentinelRef.current.released) return;
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
      } catch {
        // Low battery, or the page lost visibility mid-request. Nothing to do.
      }
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      void sentinel?.release().catch(() => {});
    };
  }, [active]);
}
